// Scope business logic — framework-agnostic, zero React imports

import type { ScopeContext, ScopeFilter, ScopeOption, ScopeType } from './types';

/** Default scope: show everything in the tenant */
export const DEFAULT_SCOPE: ScopeContext = {
  scopeType: 'all',
  scopeEntity: null,
  scopeLabel: 'scope.all',
};

/** Predefined scope options for the switcher (static entries) */
export const SCOPE_OPTIONS: ScopeOption[] = [
  { type: 'all', labelKey: 'scope.all', icon: 'globe' },
  { type: 'personal', labelKey: 'scope.personal', icon: 'user' },
];

/** URL search parameter keys used for scope serialization */
const PARAM_SCOPE_TYPE = 'scope';
const PARAM_ENTITY_ID = 'scope_id';
const PARAM_ENTITY_NAME = 'scope_name';
const PARAM_ENTITY_TYPE = 'scope_entity_type';

/** Valid scope types for runtime validation */
const VALID_SCOPE_TYPES = new Set<ScopeType>(['all', 'personal', 'team', 'project', 'entity']);

/**
 * Converts a scope context into API filter parameters.
 *
 * - `all` returns tenant-wide filter
 * - `personal` returns a user_id filter (caller must resolve actual ID)
 * - `team` / `project` / `entity` returns parent scoping by entity
 */
export function getScopeFilter(scope: ScopeContext): ScopeFilter {
  switch (scope.scopeType) {
    case 'all':
      return { tenant_only: true };

    case 'personal':
      return { user_id: scope.scopeEntity?.id ?? '' };

    case 'team':
    case 'project':
    case 'entity':
      if (!scope.scopeEntity) {
        return { tenant_only: true };
      }
      return {
        parent_id: scope.scopeEntity.id,
        parent_type: scope.scopeEntity.type,
      };

    default:
      return { tenant_only: true };
  }
}

/** Returns the i18n key for a given scope context */
export function getScopeLabel(scope: ScopeContext): string {
  if (scope.scopeLabel) {
    return scope.scopeLabel;
  }

  switch (scope.scopeType) {
    case 'all':
      return 'scope.all';
    case 'personal':
      return 'scope.personal';
    case 'team':
      return scope.scopeEntity?.name ? 'scope.teamNamed' : 'scope.team';
    case 'project':
      return scope.scopeEntity?.name ? 'scope.projectNamed' : 'scope.project';
    case 'entity':
      return scope.scopeEntity?.name ? 'scope.entityNamed' : 'scope.entity';
    default:
      return 'scope.all';
  }
}

/**
 * Builds a ScopeContext from discrete parameter values.
 * Useful when constructing scope from route params, store data, or API responses.
 */
export function buildScopeFromParams(
  type: string,
  entityId?: string,
  entityName?: string,
  entityType?: string,
): ScopeContext {
  const scopeType: ScopeType = VALID_SCOPE_TYPES.has(type as ScopeType)
    ? (type as ScopeType)
    : 'all';

  if (scopeType === 'all') {
    return { ...DEFAULT_SCOPE };
  }

  const needsEntity = scopeType === 'team' || scopeType === 'project' || scopeType === 'entity';

  if (needsEntity && entityId) {
    return {
      scopeType,
      scopeEntity: {
        id: entityId,
        type: entityType ?? scopeType,
        name: entityName ?? '',
      },
      scopeLabel: getScopeLabel({
        scopeType,
        scopeEntity: { id: entityId, type: entityType ?? scopeType, name: entityName ?? '' },
        scopeLabel: '',
      }),
    };
  }

  if (scopeType === 'personal') {
    return {
      scopeType: 'personal',
      scopeEntity: entityId
        ? { id: entityId, type: entityType ?? 'user', name: entityName ?? '' }
        : null,
      scopeLabel: 'scope.personal',
    };
  }

  return { ...DEFAULT_SCOPE };
}

/**
 * Serializes a scope context to URL-safe query parameters.
 * Only includes non-empty values.
 */
export function serializeScopeToParams(scope: ScopeContext): Record<string, string> {
  const params: Record<string, string> = {};

  if (scope.scopeType && scope.scopeType !== 'all') {
    params[PARAM_SCOPE_TYPE] = scope.scopeType;
  }

  if (scope.scopeEntity) {
    if (scope.scopeEntity.id) {
      params[PARAM_ENTITY_ID] = scope.scopeEntity.id;
    }
    if (scope.scopeEntity.name) {
      params[PARAM_ENTITY_NAME] = scope.scopeEntity.name;
    }
    if (scope.scopeEntity.type) {
      params[PARAM_ENTITY_TYPE] = scope.scopeEntity.type;
    }
  }

  return params;
}

/** Parses a ScopeContext from URL search parameters */
export function parseScopeFromUrl(searchParams: URLSearchParams): ScopeContext {
  const type = searchParams.get(PARAM_SCOPE_TYPE) ?? '';
  const entityId = searchParams.get(PARAM_ENTITY_ID) ?? undefined;
  const entityName = searchParams.get(PARAM_ENTITY_NAME) ?? undefined;
  const entityType = searchParams.get(PARAM_ENTITY_TYPE) ?? undefined;

  if (!type) {
    return { ...DEFAULT_SCOPE };
  }

  return buildScopeFromParams(type, entityId, entityName, entityType);
}

/** Compares two scope contexts for equality */
export function isScopeEqual(a: ScopeContext, b: ScopeContext): boolean {
  if (a.scopeType !== b.scopeType) {
    return false;
  }

  if (a.scopeEntity === null && b.scopeEntity === null) {
    return true;
  }

  if (a.scopeEntity === null || b.scopeEntity === null) {
    return false;
  }

  return (
    a.scopeEntity.id === b.scopeEntity.id &&
    a.scopeEntity.type === b.scopeEntity.type
  );
}
